import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import React, { Suspense, useContext, useEffect, useRef, useState } from "react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ICalim_V2_DetailGridProps } from "../../../types/claim_V2_DetailsGridProps.types";
import { mongoDBDateParserToUSAFormat_v2, monthDayFormatterForWLTQuery } from "../../../utils/commonFunctions/date";
import { Avatar, Button, Chip } from "@mui/material";
import { IClaim_V2_Details } from "../../../types/claim_v2.types";
import { AuthContext, ThemeContext } from "../../../contexts";
import ProfileSubHeader from "../profile/profileSubHeader/ProfileSubHeader";

import "./claimStatusDetailsGrid_v2.css";
import initCapitalize from "../../../utils/commonFunctions/initCapitalize";
import { maskedSSN } from "../../../constants/maskSSN";

const ClaimStatusDetailsGrid_v2: React.FC<ICalim_V2_DetailGridProps> = ({
	memberDetails,
	claimStatusList
}: any): JSX.Element => {
	const { member } = useContext(AuthContext);
	const { theme } = useContext(ThemeContext);
	// console.log("membering", member);
	const [gridHeight, setGridHeight] = useState("400px");
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const agGridRef = useRef<any>(null);

	const handleAgGridReady = (params: GridReadyEvent) => {
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
		const { api, columnApi } = params;
		api.sizeColumnsToFit();
	};

	const numberToDollarStr = ({ value }: { value: number }) => {
		// console.log("CLAIM_DOLLAR--->", value);
		let strValue = "$ " + String(value);
		if (!strValue.includes(".")) {
			strValue = strValue + ".00";
		}
		return strValue;
	};

	const handleDateCellRenderer = ({ value }: { value: string }) => {
		if (value) {
			return mongoDBDateParserToUSAFormat_v2(value);
		}
		return "NA";
	};
	const handleStatusCellRenderer = ({ value }: { value: string | null }) => {
		// console.log("=====>status cell", value);
		if (value) {
			return "PAID";
		}
		return "UNPAID";
	};

	const handleNullValueRenderer = ({ value }: { value: string }) => {
		if (value) {
			return value;
		}
		return "N/A";
	};

	const handleActionCellRender = ({ data }: { data: IClaim_V2_Details }) => {
		const claim_no = window.btoa(String(data.CLMNO));
		const claim_prefix = window.btoa(String(data.CLMPRE));
		const claim_suffix = window.btoa(String(data.CLMSUF));
		const groupNumber = memberDetails?.GRPNO || "unknown";
		// console.log("GroupHN-->", groupNumber);

		const handleNavigateToEOB = () => {
			if (data.EECKNO && data.PRVCKNO) {
				const providerCheckNumber = window.btoa(String(data.PRVCKNO));
				const url = `/claim_v2-status/EOB?gn=${groupNumber}&pckn=${providerCheckNumber}`;
				window.open(url, "_blank");
			} else if (data.STATUS === "Closed") {
				const url = `/claim_v2-status/EOB?cn=${claim_no}&cp=${claim_prefix}&cs=${claim_suffix}&gn=${groupNumber}`;
				window.open(url, "_blank");
			} else {
				const url = `/claim_v2-status/EOB?cn=${claim_no}&cp=${claim_prefix}&cs=${claim_suffix}&gn=${groupNumber}`;
				window.open(url, "_blank");
			}
		};

		return (
			<>
				{data.PRVCKNO ? (
					<Button
						variant="outlined"
						style={{ fontSize: 12, padding: 2, paddingLeft: 5, paddingRight: 5 }}
						onClick={handleNavigateToEOB}
					>
						Provider EOB
					</Button>
				) : data.STATUS === "Closed" ? (
					<Button
						variant="outlined"
						style={{ fontSize: 12, padding: 2, paddingLeft: 5, paddingRight: 5 }}
						onClick={handleNavigateToEOB}
					>
						Provider EOB
					</Button>
				) : (
					"N/A"
				)}
			</>
		);
	};

	const handleEmployeeEOBActionCellRender = ({ data }: { data: IClaim_V2_Details }) => {
		const claim_no = window.btoa(String(data.CLMNO));
		const claim_prefix = window.btoa(String(data.CLMPRE));
		const claim_suffix = window.btoa(String(data.CLMSUF));
		const handleNavigateToEOB = () => {
			if (data.EECKNO && data.PRVCKNO) {
				const employeeCheckNumber = window.btoa(String(data.EECKNO));
				const url = `/claim_v2-status/EOB?gn=${memberDetails?.GRPNO}&eckn=${employeeCheckNumber}`;
				window.open(url, "_blank");
			} else {
				const url = `/claim_v2-status/EOB?cn=${claim_no}&cp=${claim_prefix}&cs=${claim_suffix}&gn=${memberDetails?.GRPNO}`;
				window.open(url, "_blank");
			}
		};
		return (
			<>
				{data.EECKNO ? (
					<Button
						variant="outlined"
						style={{ fontSize: 12, padding: 2, paddingLeft: 5, paddingRight: 5 }}
						onClick={handleNavigateToEOB}
					>
						Employee EOB
					</Button>
				) : (
					"N/A"
				)}
			</>
		);
	};

	const claimProcessedData = claimStatusList?.map((item: any) => ({
		...item,
		CLM_FULL: `${item.CLMPRE}-${
			String(item.CLMNO).length === 7
				? `00${item.CLMNO}`
				: String(item.CLMNO).length === 8
				? `0${item.CLMNO}`
				: item.CLMNO
		}-${
			item.CLMSUF === 0
				? "0000"
				: String(item.CLMSUF).length === 2
				? `00${item.CLMSUF}`
				: String(item.CLMSUF).length === 3
				? `0${item.CLMSUF}`
				: item.CLMSUF
		}`
	}));

	const columnDefs = [
		{
			field: "FROMDT",
			headerName: "Statement From",
			cellRenderer: "handleDateCellRenderer",
			suppressSizeToFit: true,
			floatingFilterComponentParams: { suppressFilterButton: true }
		},
		{
			field: "THRUDT",
			headerName: "Statement To",
			cellRenderer: "handleDateCellRenderer",
			suppressSizeToFit: true,
			floatingFilterComponentParams: { suppressFilterButton: true }
		},
		{
			field: "NAME",
			headerName: "Provider Name",
			suppressSizeToFit: true,
			floatingFilterComponentParams: { suppressFilterButton: true }
		},
		{
			field: "CLM_FULL",
			headerName: "Claim Number",
			suppressSizeToFit: true,
			floatingFilterComponentParams: { suppressFilterButton: true }
		},
		{
			field: "STATUS",
			headerName: "Status",
			floatingFilterComponentParams: { suppressFilterButton: true },
			suppressSizeToFit: true
			// cellRenderer: "handleStatusCellRenderer"
		},
		{
			field: "PDDT",
			headerName: "Paid Date",
			cellRenderer: "handleDateCellRenderer",
			suppressSizeToFit: true
		},
		{
			field: "CHGAMT",
			headerName: "Total Charges",
			suppressSizeToFit: true,
			cellRenderer: "numberToDollarStr"
		},
		{
			field: "PRVPMT",
			headerName: "Provider Payment",
			cellRenderer: "numberToDollarStr",
			suppressSizeToFit: true,
			filter: false
		},
		{
			field: "PRVCKNO",
			headerName: "Provider Check No",
			cellRenderer: "handleNullValueRenderer",
			suppressSizeToFit: true
		},
		{
			field: "EEPMT",
			headerName: "Employee Payment",
			cellRenderer: "numberToDollarStr",
			suppressSizeToFit: true,
			filter: false
		},
		{
			field: "EECKNO",
			headerName: "Employee Check No",
			cellRenderer: "handleNullValueRenderer",
			suppressSizeToFit: true,
			filter: false
		},
		{
			field: "EMP_RESP",
			headerName: "Employee Responsibility",
			cellRenderer: "numberToDollarStr",
			suppressSizeToFit: true,
			filter: false
		},
		{
			field: "PRV_RESP",
			headerName: "Provider Responsibility",
			cellRenderer: "numberToDollarStr",
			suppressSizeToFit: true,
			filter: false
		},
		{
			field: "action",
			headerName: "Generate Provider EOB",
			cellRenderer: "handleActionCellRender",
			suppressSizeToFit: true,
			filter: false
		},
		{
			field: "action",
			headerName: "Generate Employee EOB",
			cellRenderer: "handleEmployeeEOBActionCellRender",
			suppressSizeToFit: true,
			filter: false
		}
	];
	// console.log(memberDetails);
	if (!memberDetails && claimStatusList != 0) return <>Fetching claims please wait...</>;
	return (
		<div className="claim-status-details-grid-v2" id="claim-status-details-grid-v2">
			<div className="theme-profile-header-v2">
				<div className="claims-member-details-header">
					{/* <div className="claims-v2-member-name">{member?.first_name + " " + member?.last_name}</div> */}
					{/* <Chip
						label={initCapitalize(member?.role ? String(member.role) : "")}
						style={{
							backgroundColor: theme.primary_color,
							color: "white",
							margin: "0.4rem 0"
						}}
						variant="filled"
						className="claims-v2-member-role-chip-container"
					/> */}
					<div className="theme-profile-header-v2">
						<div className="theme-name">{member?.first_name + " " + member?.last_name}</div>
						<div className="theme-avatar-meta-cont">
							<Avatar className="theme-avatar">{member?.first_name[0] + member?.last_name[0]}</Avatar>
							<div className="theme-assign-info">
								<div className="theme-group-name">{member?.group?.name}</div>
								<div className="theme-location-name">{memberDetails?.location_name}</div>
							</div>
						</div>
					</div>
					<div className="claims-v2-member-details-grid-container">
						<div className="claims-v2-left-container">
							{/* <div className="row">
								<div className="claims-v2-value">
									{initCapitalize(member?.employee_status ? member.employee_status : "")}
								</div>
								<div className="claims-v2-label">Employee Status</div>
							</div> */}
							{/* <div className="row">
							<div className="claims-v2-value">
								{member?.date_of_birth.substring(5, 7) +
									"-" +
									member?.date_of_birth.substring(8, 10) +
									"-" +
									member?.date_of_birth.substring(0, 4)}
							</div>
							<div className="claims-v2-label">Date Of Birth</div>
						</div> */}
						</div>
						<div className="claims-v2-right-container">
							{/* <div className="row">
								<div className="claims-v2-value">{maskedSSN(member?.SSN)}</div>
								<div className="claims-v2-label">SSN</div>
							</div> */}
							{/* <div className="row">
							<div className="claims-v2-value">{memberDetails?.taxID}</div>
							<div className="claims-v2-label">Tax ID</div>
						</div> */}
						</div>
					</div>
				</div>
			</div>
			<Suspense fallback={<div />}>
				<ProfileSubHeader title={"Claims"} actionButtons={[]} style={{ marginBottom: 0 }} />
			</Suspense>
			<div className="ag-grid-main-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						columnDefs={columnDefs}
						rowData={claimProcessedData}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						animateRows
						paginationPageSize={6}
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						enableCellChangeFlash={true}
						defaultColGroupDef={{ marryChildren: true }}
						frameworkComponents={{
							handleDateCellRenderer,
							handleStatusCellRenderer,
							handleActionCellRender,
							handleEmployeeEOBActionCellRender,
							handleNullValueRenderer,
							numberToDollarStr
						}}
						rowSelection={"single"}
					></AgGridReact>
				</div>
			</div>
		</div>
	);
};

export default ClaimStatusDetailsGrid_v2;
